// Username validation utilities

export const USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;

export const validateUsername = (username: string): { isValid: boolean; error?: string } => {
  const trimmedUsername = username.trim();
  
  if (!trimmedUsername) {
    return { isValid: false, error: "Nome de usuário é obrigatório" };
  }
  
  if (trimmedUsername.length < 5) {
    return { isValid: false, error: "O usuário deve ter pelo menos 5 caracteres" };
  }
  
  if (trimmedUsername.length > 20) {
    return { isValid: false, error: "Nome de usuário deve ter no máximo 20 caracteres" };
  }
  
  // Check for auto-generated usernames (user_xxx pattern)
  if (/^user_/i.test(trimmedUsername)) {
    return { isValid: false, error: "O usuário deve ter pelo menos 5 caracteres" };
  }
  
  // Check if username is only numbers
  if (/^\d+$/.test(trimmedUsername)) {
    return { isValid: false, error: "O nome de usuário não pode ter somente números" };
  }
  
  if (!USERNAME_REGEX.test(trimmedUsername)) {
    return { isValid: false, error: "O nome de usuário não pode ter caracteres especiais" };
  }
  
  return { isValid: true };
};

// Content validation utilities
export const validateMeaningfulContent = (content: string, fieldName: string): { isValid: boolean; error?: string } => {
  const trimmedContent = content.trim();
  
  if (!trimmedContent) {
    return { isValid: false, error: `${fieldName} é obrigatório` };
  }
  
  if (trimmedContent.length < 10) {
    return { isValid: false, error: `${fieldName} deve ter pelo menos 10 caracteres` };
  }
  
  // Check for meaningless content (only dots, spaces, dashes, etc.)
  const meaninglessPattern = /^[\s\.\-_=\+*#@!?]*$/;
  if (meaninglessPattern.test(trimmedContent)) {
    return { isValid: false, error: `Por favor, forneça informações úteis em ${fieldName}` };
  }
  
  // Check for repeated characters (more lenient)
  const repeatedCharPattern = /(.)\1{7,}/; // Increased from 5 to 7
  if (repeatedCharPattern.test(trimmedContent)) {
    return { isValid: false, error: `${fieldName} não pode conter caracteres repetidos em excesso` };
  }
  
  return { isValid: true };
};

import { isValidPortugueseText, detectGibberish, calculatePortugueseScore, removeEmojis, countEmojis } from './portugueseLanguageUtils';

// Enhanced bio validation with much more permissive approach
export const validateBio = (bio: string): { isValid: boolean; error?: string } => {
  const trimmedBio = bio.trim();
  
  if (!trimmedBio) {
    return { isValid: false, error: "Biografia é obrigatória" };
  }
  
  if (trimmedBio.length < 15) { // Reduced from 20 to 15
    return { isValid: false, error: "Biografia deve ter pelo menos 15 caracteres" };
  }
  
  if (trimmedBio.length > 500) {
    return { isValid: false, error: "Biografia deve ter no máximo 500 caracteres" };
  }

  // Remove emojis for text analysis
  const textWithoutEmojis = removeEmojis(trimmedBio);
  const emojiCount = countEmojis(trimmedBio);

  // 1. Check for repetitive patterns (like :):):):):)) - but more lenient
  const repetitivePattern = /(.{1,3})\1{5,}/; // Increased from 4 to 5
  if (repetitivePattern.test(textWithoutEmojis)) {
    return { isValid: false, error: "Biografia não pode conter padrões repetitivos. Escreva sobre você de forma única!" };
  }

  // 2. More lenient emoji/symbol handling
  const totalLength = trimmedBio.length;
  const textLength = textWithoutEmojis.length;
  const symbolRatio = (totalLength - textLength) / totalLength;
  
  // Only reject if it's mostly symbols/emojis with very little text
  if (symbolRatio > 0.7 && textLength < 5) {
    return { isValid: false, error: "Biografia deve conter mais texto além de símbolos e emojis!" };
  }

  // 3. Check for consecutive symbols/emojis (more lenient)
  const consecutiveSymbols = /[^\w\s]{6,}/; // Increased from 4 to 6
  if (consecutiveSymbols.test(trimmedBio)) {
    return { isValid: false, error: "Evite usar muitos símbolos consecutivos. Descreva-se com palavras!" };
  }

  // 4. Enhanced word diversity check (more lenient)
  const words = textWithoutEmojis.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 1);
  
  const uniqueWords = new Set(words);
  const wordDiversityRatio = uniqueWords.size / Math.max(words.length, 1);
  
  if (words.length < 2) { // Reduced from 4 to 2
    return { isValid: false, error: "Biografia deve conter pelo menos 2 palavras diferentes" };
  }
  
  // More lenient diversity check
  if (wordDiversityRatio < 0.4 && words.length > 8) { // More lenient (was 0.6 and 10)
    return { isValid: false, error: "Use palavras mais variadas para se descrever!" };
  }

  // 5. Check for keyboard mashing and gibberish (more lenient)
  if (detectGibberish(textWithoutEmojis)) {
    return { isValid: false, error: "Biografia parece conter texto aleatório. Escreva sobre você de forma clara!" };
  }

  // 6. Much more lenient Portuguese language validation
  const portugueseValidation = isValidPortugueseText(textWithoutEmojis);
  
  if (!portugueseValidation.isValid && portugueseValidation.issues.length > 0) {
    const primaryIssue = portugueseValidation.issues[0];
    // Only fail on serious issues
    if (primaryIssue.includes('aleatório') || primaryIssue.includes('sem sentido')) {
      return { isValid: false, error: "Biografia parece conter texto sem sentido. Escreva sobre você!" };
    }
    // Don't fail on language issues anymore - be very permissive
  }

  // 7. Much more lenient Portuguese score requirement
  const portugueseScore = calculatePortugueseScore(textWithoutEmojis);
  if (portugueseScore < 15 && textLength > 20) { // Much more lenient (was 40)
    return { isValid: false, error: "Escreva de forma mais clara e compreensível" };
  }

  // 8. Check for meaningful sentences (more lenient)
  const sentences = textWithoutEmojis.split(/[.!?]+/).filter(s => s.trim().length > 3); // Reduced from 5 to 3
  const hasMinimalStructure = sentences.length >= 1 || textLength >= 10;
  
  if (!hasMinimalStructure) {
    return { isValid: false, error: "Biografia deve conter pelo menos uma frase ou informação clara" };
  }

  // 9. More lenient vowel/consonant ratio check
  const letters = textWithoutEmojis.toLowerCase().match(/[a-záàâãéèêíìîóòôõúùû]/g) || [];
  const vowels = textWithoutEmojis.toLowerCase().match(/[aáàâãeéèêiíìîoóòôõuúùû]/g) || [];
  const vowelRatio = vowels.length / Math.max(letters.length, 1);
  
  if (letters.length > 15 && (vowelRatio < 0.1 || vowelRatio > 0.9)) { // More lenient (was 10 letters)
    return { isValid: false, error: "Biografia não parece conter texto válido. Escreva normalmente sobre você!" };
  }

  // 10. Check for meaningless content (unchanged)
  const meaninglessPattern = /^[\s\.\-_=\+*#@!?]*$/;
  if (meaninglessPattern.test(textWithoutEmojis)) {
    return { isValid: false, error: "Por favor, escreva uma biografia significativa sobre você" };
  }
  
  // 11. More permissive consecutive spaces check
  const normalizedBio = trimmedBio.replace(/\s+/g, ' ');
  const consecutiveSpacesPattern = /\s{8,}/; // Increased from 5 to 8
  if (consecutiveSpacesPattern.test(normalizedBio)) {
    return { isValid: false, error: "Biografia não pode conter muitos espaços consecutivos" };
  }
  
  return { isValid: true };
};

// Professional field validation
export const validateProfessionalField = (value: string, fieldName: string): { isValid: boolean; error?: string } => {
  const trimmedValue = value.trim();
  
  if (!trimmedValue) {
    return { isValid: false, error: `${fieldName} é obrigatório` };
  }
  
  if (trimmedValue.length < 3) {
    return { isValid: false, error: `${fieldName} deve ter pelo menos 3 caracteres` };
  }
  
  if (trimmedValue.length > 100) {
    return { isValid: false, error: `${fieldName} deve ter no máximo 100 caracteres` };
  }
  
  // Check for meaningless content
  const meaninglessPattern = /^[\s\.\-_=\+*#@!?]*$/;
  if (meaninglessPattern.test(trimmedValue)) {
    return { isValid: false, error: `Por favor, forneça informações válidas para ${fieldName}` };
  }
  
  return { isValid: true };
};

// Sanitization functions
export const trimAndSanitizeUsername = (username: string): string => {
  return username.trim().replace(/\s+/g, '').toLowerCase();
};

export const sanitizeTextContent = (content: string): string => {
  return content
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/^\s+|\s+$/g, '') // Remove leading/trailing spaces
    .substring(0, 2000); // Limit length (alinhado com textContentSchema)
};

export const sanitizeProfessionalField = (value: string): string => {
  return value
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^\s+|\s+$/g, '')
    .substring(0, 200); // Limit length (alinhado com professionalFieldSchema)
};

// Profile validation utility
export const validateProfileData = (data: {
  bio?: string;
  occupation?: string;
  education?: string;
}): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  
  if (data.bio !== undefined) {
    const bioValidation = validateBio(data.bio);
    if (!bioValidation.isValid && bioValidation.error) {
      errors.bio = bioValidation.error;
    }
  }
  
  if (data.occupation !== undefined) {
    const occupationValidation = validateProfessionalField(data.occupation, 'Profissão');
    if (!occupationValidation.isValid && occupationValidation.error) {
      errors.occupation = occupationValidation.error;
    }
  }
  
  if (data.education !== undefined) {
    const educationValidation = validateProfessionalField(data.education, 'Formação');
    if (!educationValidation.isValid && educationValidation.error) {
      errors.education = educationValidation.error;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
